import { useEffect } from 'react';
import { css } from '@emotion/react';
import { AuthMethod, Contact } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';
import { MFAMethodQuickActions } from './mfa-method-quick-actions';

const styles = {
  root: css`
    max-height: 200px;
    overflow-y: auto;
    margin-bottom: ${theme.spacing(2)};
  `,
  contactContainer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(1, 0)};
    align-items: center;
    border-bottom: 1px solid ${theme.colors.neutral20};
    justify-content: space-between;
  `,
  contactText: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 250px;
  `,
};

type MFAContactsProps = {
  mfaContacts: Contact[];
  refetch: () => Promise<any>;
};

/**
 * For managing MFA methods in Settings
 * NOTE this was copied over from the old MFAContacts with some changes
 */
export const ManageMFAMethods = ({ mfaContacts, refetch }: MFAContactsProps) => {
  const { t } = useTranslation('account');
  const canDeleteMFAMethods = mfaContacts.length > 1;
  const primaryContactId =
    mfaContacts.find((contact: Contact) => contact.state === 'primary')?.contactId ?? mfaContacts[0]?.contactId ?? '';

  // in this view we want to refetch the user details when the component mounts
  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div css={styles.root}>
      <Text size='medium'>{t('Active')}</Text>

      {mfaContacts &&
        mfaContacts.map((contact: Contact) => (
          <div css={styles.contactContainer} key={contact.contactId}>
            <Icon name={getIcon(contact, primaryContactId)} color='primary' size={24} />
            <div
              css={css`
                text-align: center;
              `}
            >
              <ContactName contact={contact} />
              {contact.state === 'not validated' && (
                <Text as='span' color='error' size='small' weight='semibold'>
                  {t('Not Validated')}
                </Text>
              )}
            </div>
            <MFAMethodQuickActions
              primaryContactId={primaryContactId}
              contact={contact}
              canDeleteMFAMethods={canDeleteMFAMethods}
              refetch={refetch}
            />
          </div>
        ))}
    </div>
  );
};

function getIcon(contact: Contact, primaryContactId: string) {
  if (contact.contactId === primaryContactId) {
    return 'star-filled-small';
  }
  switch (contact.type) {
    case AuthMethod.EMAIL:
      return 'email';
    case AuthMethod.PHONE:
      return 'message';
    default:
      return 'message';
  }
}

type ContactNameProps = {
  contact: Contact;
};

const ContactName = ({ contact }: ContactNameProps) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  return (
    <>
      <Text weight='bold' {...triggerProps} css={styles.contactText}>
        {contact.type === AuthMethod.PHONE ? formatPhoneNumber(contact.contact) : contact.contact}
      </Text>
      <Tooltip {...tooltipProps}>{contact.contact}</Tooltip>
    </>
  );
};
